import React, { useState } from 'react'
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography,
} from '@mui/material'
import {
  AdvancedCareTeamMember,
  AdvancedTrackDefinition,
  AdvancedTrackEndReason,
  AdvancedTrackLead,
  AdvancedTrackSummary,
} from '../../api/types'
import { advancedColors } from '../../theme'
import { roleLabelFor } from '../careTeam/roleCategories'
import LeadPicker from './LeadPicker'
import { defaultLeadFor, findTrack, trackTitle } from './trackTransitions'
import { useLeadDirectory } from './useLeadDirectory'

// `next` set means a switch: the other track starts as this one ends, in one step.
export type EndTrackRequest = {
  track: AdvancedTrackSummary
  endReason?: AdvancedTrackEndReason
  next?: string | null
  // A lead the plan already names for `next`; otherwise the owner-role default.
  nextLead?: AdvancedTrackLead
  // Runs once the service has ended it.
  onEnded?: () => void
}

type Props = {
  request: EndTrackRequest | null
  catalog: AdvancedTrackDefinition[]
  careTeam: AdvancedCareTeamMember[]
  onCancel: () => void
  onConfirm: (
    track: AdvancedTrackSummary,
    endReason: AdvancedTrackEndReason,
    next: string | null,
    nextLead: AdvancedTrackLead
  ) => void
}

type FormProps = Omit<Props, 'request'> & { request: EndTrackRequest }

// Mounted per request, so each open starts from that request's presets.
const EndTrackForm = ({ request, catalog, careTeam, onCancel, onConfirm }: FormProps) => {
  const directory = useLeadDirectory(careTeam)
  const next = request.next ?? null
  const nextDefinition = next ? findTrack(catalog, next) : undefined
  const [endReason, setEndReason] = useState<AdvancedTrackEndReason>(request.endReason ?? 'completed')
  const [nextLead, setNextLead] = useState<AdvancedTrackLead>(() => {
    if (request.nextLead !== undefined) {
      return request.nextLead
    }
    const providerUid = defaultLeadFor(nextDefinition, careTeam)
    return providerUid ? { providerUid, joinAs: null } : null
  })

  const title = trackTitle(catalog, request.track.trackKey)
  const nextTitle = next ? trackTitle(catalog, next) : null

  return (
    <Dialog open onClose={onCancel} maxWidth="xs" fullWidth>
      <DialogTitle>{nextTitle ? `Switch to ${nextTitle}` : `End ${title}`}</DialogTitle>
      <DialogContent>
        <Typography variant="subtitle2" sx={{ color: advancedColors.inkMuted, mb: 0.5 }}>
          How did {title} end?
        </Typography>
        <RadioGroup
          value={endReason}
          onChange={(event) => setEndReason(event.target.value as AdvancedTrackEndReason)}
          sx={{ mb: 2 }}
        >
          <FormControlLabel value="completed" control={<Radio size="small" />} label="Completed" />
          <FormControlLabel value="discontinued" control={<Radio size="small" />} label="Closed without completion" />
        </RadioGroup>

        {nextTitle && (
          <LeadPicker
            options={directory.optionsFor(nextDefinition)}
            value={nextLead}
            onChange={setNextLead}
            label={`${nextTitle} led by`}
            missingRole={
              nextDefinition?.ownerRole && !defaultLeadFor(nextDefinition, careTeam)
                ? roleLabelFor(nextDefinition.ownerRole)
                : null
            }
          />
        )}

        <Typography variant="body2" sx={{ color: advancedColors.inkMuted, mt: 2, mb: 2 }}>
          Completed content stays in the patient&apos;s record either way.
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
          <Button onClick={onCancel}>Cancel</Button>
          <Button
            variant="contained"
            color={endReason === 'discontinued' && !nextTitle ? 'error' : 'primary'}
            onClick={() => onConfirm(request.track, endReason, next, next ? nextLead : null)}
          >
            {nextTitle ? `End and start ${nextTitle}` : 'End track'}
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  )
}

const EndTrackDialog = ({ request, ...rest }: Props) => (request ? <EndTrackForm request={request} {...rest} /> : null)

export default EndTrackDialog
